/**
 * SL feedback page helpfulness evaluation — Firestore schema and paths.
 *
 * One document per user per section week, living alongside the other
 * section-analysis leaf collections under a per-course, per-section,
 * per-week namespace:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slFeedbackEvaluation/{userId}`
 *
 * Prior snapshots (when the user changes vote or the linked `slFeedback` row
 * changes) are appended under:
 * `.../slFeedbackEvaluation/{userId}/history/{historyId}`
 *
 * NOTE: the `{courseId}` segment is intentional — it lets Firestore rules
 * perform course-role checks without a cross-document `get()` on the section
 * doc. See `sectionAnalysisFirestorePaths.ts` for the shared layout.
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import type {SectionId, SectionWeekId} from '../../../section/sectionApi';
import type {CourseId} from '../../../types/branded';
import {
  getSectionAnalysisWeekChildCollectionPath,
  SECTION_ANALYSIS_SL_FEEDBACK_EVALUATION_SUBCOLLECTION,
} from './sectionAnalysisFirestorePaths';

export const SL_FEEDBACK_HISTORY_COLLECTION_ID = 'history';

/**
 * Must match the Cloud Functions export name (Firebase callable id), e.g.
 * `export const saveslfeedbackevaluation = onCall(...)`.
 */
export const SAVE_SL_FEEDBACK_EVALUATION_FUNCTION_NAME =
  'saveslfeedbackevaluation';

// ============================================================================
// Helpfulness
// ============================================================================

/** Thumbs-up / thumbs-down response for “Was this page helpful?”. */
export const SlFeedbackHelpfulSchema = z.enum(['up', 'down']);

export type SlFeedbackHelpful = z.infer<typeof SlFeedbackHelpfulSchema>;

// ============================================================================
// Timestamps on read
// ============================================================================

/**
 * Timestamp fields on evaluation documents — same structural validation as
 * other Firestore timestamp fields. (Avoid `z.date()` here: Firestore returns
 * `Timestamp`, and union fall-through produced confusing “expected date”
 * errors for missing values.)
 */
export const SlFeedbackEvaluationTimestampSchema = FirestoreTimestampSchema;

// ============================================================================
// Evaluation document
// ============================================================================

/**
 * Schema for an SL feedback page evaluation document.
 *
 * Uses `.loose()` to allow additional Firestore metadata fields.
 *
 * Denormalized path fields (`courseId`, `sectionId`, `sectionWeek`) are
 * optional for back-compat with documents written before the denorm migration.
 * New writes always stamp all three. Use these for `collectionGroup` queries
 * filtered by course.
 */
export const SlFeedbackEvaluationDocumentSchema = z
  .object({
    /** User’s helpfulness vote. */
    helpful: SlFeedbackHelpfulSchema,
    /** Whether LLM-generated section feedback was enabled when they voted. */
    llmFeedbackEnabled: z.boolean(),
    /**
     * Server time of the last write to this evaluation row. Prior versions keep
     * their `updatedAt` on `history` docs; with `archivedAt`, the earliest
     * `updatedAt` across current + history is the first submission time.
     */
    updatedAt: SlFeedbackEvaluationTimestampSchema,
    /**
     * Firestore doc id of the `slFeedback` document shown, if any.
     * Model and generation time live on that document (`model`, `generatedAt`).
     * `null` clears linkage when LLM feedback is off (avoid `deleteField` in
     * payloads that round-trip through the Zod converter).
     */
    slFeedbackId: z.string().nullable().optional(),
    /**
     * Denormalized course identifier. Optional for back-compat with documents
     * written before the denorm migration; all new writes include this field.
     * Enables efficient `collectionGroup` queries filtered by course.
     */
    courseId: z.string().optional(),
    /**
     * Denormalized section identifier. Optional for back-compat with documents
     * written before the denorm migration; all new writes include this field.
     */
    sectionId: z.string().optional(),
    /**
     * Denormalized week number. Optional for back-compat with documents written
     * before the denorm migration; all new writes include this field.
     */
    sectionWeek: z.number().optional(),
  })
  .loose();

/** Stored evaluation for the section feedback page (per user, per week). */
export type SlFeedbackEvaluationDocument = z.infer<
  typeof SlFeedbackEvaluationDocumentSchema
>;

/**
 * Archived copy of a prior evaluation (same fields as the parent doc, plus when
 * it was superseded).
 */
export const SlFeedbackEvaluationHistoryDocumentSchema =
  SlFeedbackEvaluationDocumentSchema.extend({
    archivedAt: SlFeedbackEvaluationTimestampSchema,
  });

export type SlFeedbackEvaluationHistoryDocument = z.infer<
  typeof SlFeedbackEvaluationHistoryDocumentSchema
>;

// ============================================================================
// Callable payloads
// ============================================================================

/** Callable request payload to persist a user helpfulness evaluation. */
export const SaveSlFeedbackEvaluationRequestSchema = z.object({
  courseId: z.string().min(1),
  sectionId: z.string().min(1),
  sectionWeek: z.number().int().positive(),
  helpful: SlFeedbackHelpfulSchema,
  llmFeedbackEnabled: z.boolean(),
  slFeedbackId: z.string().nullable(),
});

export type SaveSlFeedbackEvaluationRequest = z.infer<
  typeof SaveSlFeedbackEvaluationRequestSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Collection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slFeedbackEvaluation`
 */
export function getSlFeedbackEvaluationCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
}): string {
  return getSectionAnalysisWeekChildCollectionPath({
    courseId,
    sectionId,
    sectionWeek,
    leafSubcollection: SECTION_ANALYSIS_SL_FEEDBACK_EVALUATION_SUBCOLLECTION,
  });
}

/**
 * Document path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slFeedbackEvaluation/{userId}`
 */
export function getSlFeedbackEvaluationDocumentPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: string;
}): string {
  return `${getSlFeedbackEvaluationCollectionPath({courseId, sectionId, sectionWeek})}/${userId}`;
}

/**
 * Subcollection path:
 * `sectionAnalysis/{courseId}/sections/{sectionId}/sectionWeeks/{sectionWeek}/slFeedbackEvaluation/{userId}/history`
 */
export function getSlFeedbackEvaluationHistoryCollectionPath({
  courseId,
  sectionId,
  sectionWeek,
  userId,
}: {
  courseId: CourseId;
  sectionId: SectionId;
  sectionWeek: SectionWeekId;
  userId: string;
}): string {
  return `${getSlFeedbackEvaluationDocumentPath({courseId, sectionId, sectionWeek, userId})}/history`;
}
